import { Component } from '@theme/component';

/** Retraso al salir con el mouse, para poder cruzar hacia el panel sin que se cierre */
const CLOSE_DELAY = 200;

/**
 * Mega menú de escritorio del encabezado Elife.
 * Sin JS, el CSS abre los paneles con :hover / :focus-within. Con JS, este componente
 * marca `data-enhanced` y controla `[data-open]` y `aria-expanded` (patrón disclosure).
 *
 * @extends {Component}
 */
class ElifeMegaMenu extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    this.dataset.enhanced = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#clearCloseTimer();
  }

  /**
   * Abre o cierra el panel del botón que disparó el evento.
   * @param {MouseEvent} event
   */
  togglePanel(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('.elife-nav__item');
    const isOpen = button.getAttribute('aria-expanded') === 'true';
    this.#setOpenItem(isOpen ? null : item);
  }

  /** @param {PointerEvent} event */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const item = event.target.closest('.elife-nav__item');
    if (!item || !this.contains(item)) return;

    this.#clearCloseTimer();
    this.#setOpenItem(item);
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#clearCloseTimer();
    this.#closeTimer = window.setTimeout(() => this.#setOpenItem(null), CLOSE_DELAY);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const openItem = this.querySelector('.elife-nav__item[data-open]');
    if (!openItem) return;

    this.#setOpenItem(null);
    const toggle = openItem.querySelector('.elife-nav__toggle');
    if (toggle instanceof HTMLElement) toggle.focus();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;

    this.#setOpenItem(null);
  };

  /**
   * Deja abierto solo `item` (si tiene panel) y sincroniza aria-expanded.
   * @param {Element | null} item
   */
  #setOpenItem(item) {
    for (const navItem of this.querySelectorAll('.elife-nav__item')) {
      const open = navItem === item && navItem.hasAttribute('data-has-panel');
      navItem.toggleAttribute('data-open', open);
      navItem.querySelector('.elife-nav__toggle')?.setAttribute('aria-expanded', String(open));
    }
  }

  #clearCloseTimer() {
    if (this.#closeTimer === null) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }
}

/**
 * @typedef {Object} ElifeHeaderDrawerRefs
 * @property {HTMLDetailsElement} details - El details que abre y cierra el drawer
 * @property {HTMLButtonElement} [closeButton] - Botón de cerrar dentro del panel
 */

/**
 * Drawer móvil del encabezado Elife, construido sobre <details> para que funcione sin JS.
 * Agrega: cerrar con Escape, foco al abrir y cerrar, y cierre automático al pasar a escritorio.
 *
 * @extends {Component<ElifeHeaderDrawerRefs>}
 */
class ElifeHeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {AbortController | null} */
  #controller = null;

  #desktopQuery = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.#desktopQuery.addEventListener('change', this.#handleBreakpoint, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Se dispara con el evento toggle del details */
  handleToggle() {
    if (this.refs.details.open) this.refs.closeButton?.focus();
  }

  close() {
    const { details } = this.refs;
    if (!details.open) return;

    details.open = false;
    const summary = details.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus();
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const { details } = this.refs;
    if (!details.open) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') this.#trapFocus(event);
  };

  /**
   * Mantiene el foco dentro del panel abierto (el resto de la página queda tapado por el overlay).
   * @param {KeyboardEvent} event
   */
  #trapFocus(event) {
    const panel = this.refs.details.querySelector('.elife-drawer__panel');
    if (!panel) return;

    const focusable = [...panel.querySelectorAll('a[href], button, summary')].filter(
      (element) => element instanceof HTMLElement && element.offsetParent !== null
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return;

    const active = document.activeElement;
    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  /** @param {MediaQueryListEvent} event */
  #handleBreakpoint = (event) => {
    if (event.matches) this.refs.details.open = false;
  };
}

if (!customElements.get('elife-mega-menu')) {
  customElements.define('elife-mega-menu', ElifeMegaMenu);
}

if (!customElements.get('elife-header-drawer')) {
  customElements.define('elife-header-drawer', ElifeHeaderDrawer);
}
